import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Lock, User, Wand2, Loader2, MailCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Seo from "@/components/Seo";

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/dashboard";
  const initialTab = searchParams.get("tab") === "signup" ? "signup" : "signin";
  const prefillEmail = searchParams.get("prefillEmail") ?? "";
  const prefillName = searchParams.get("prefillName") ?? "";
  const [tab, setTab] = useState<"signin" | "signup" | "magic" | "reset">(initialTab);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState(prefillName);
  const emailLocked = !!prefillEmail;
  const [signupSentTo, setSignupSentTo] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (prefillEmail) setEmail(prefillEmail);
    if (prefillName) setFullName(prefillName);
  }, [prefillEmail, prefillName]);
  const { signUp, signIn, signInWithMagicLink, resetPassword } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await signIn(email, password);
    setLoading(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      navigate(redirectTo);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await signUp(email, password, fullName, redirectTo);
    setLoading(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setSignupSentTo(email);
      setResendCooldown(30);
    }
  };

  const handleResendConfirmation = async () => {
    if (!signupSentTo || resendCooldown > 0) return;
    setResending(true);
    const { error } = await supabase.auth.resend({ type: "signup", email: signupSentTo });
    setResending(false);
    if (error) {
      toast({ title: "Couldn't resend", description: error.message, variant: "destructive" });
    } else {
      setResendCooldown(30);
      toast({ title: "Sent again", description: "New confirmation link sent to " + signupSentTo });
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await signInWithMagicLink(email, redirectTo);
    setLoading(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Check your email", description: "We sent you a magic link." });
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await resetPassword(email);
    setLoading(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Check your email", description: "We sent you a password reset link." });
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <Seo
        title="Sign in or sign up, LineupSync"
        description="Sign in to LineupSync or create a free account to schedule matches, track RSVPs, and build lineups for your team."
        path="/auth"
        noindex
      />
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-navy relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-accent/10" />
        <div className="relative z-10 max-w-md p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-6">
            <span className="text-primary-foreground font-display font-bold text-2xl">S</span>
          </div>
          <h2 className="font-display text-3xl font-bold text-navy-foreground mb-4">
            Your team, in sync.
          </h2>
          <p className="text-navy-foreground/70 leading-relaxed">
            Schedule matches, track attendance, build lineups, and keep your whole squad connected.
          </p>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft size={16} /> Back to home
          </Link>

          {tab === "signin" && (
            <form onSubmit={handleSignIn} className="space-y-6">
              <div>
                <h1 className="font-display text-2xl font-bold mb-1">Welcome back</h1>
                <p className="text-sm text-muted-foreground">Sign in to your account</p>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="email" type="email" placeholder="you@team.com" className="pl-10" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="password" type="password" placeholder="••••••••" className="pl-10" value={password} onChange={(e) => setPassword(e.target.value)} required />
                  </div>
                </div>
              </div>
              <Button variant="hero" className="w-full" size="lg" type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
              </Button>
              <div className="space-y-3 text-center text-sm">
                <button type="button" onClick={() => setTab("magic")} className="text-primary hover:underline">Sign in with magic link</button>
                <div className="text-muted-foreground">·</div>
                <button type="button" onClick={() => setTab("reset")} className="text-muted-foreground hover:text-foreground">Forgot password?</button>
              </div>
              <p className="text-center text-sm text-muted-foreground">
                Don't have an account?{" "}
                <button type="button" onClick={() => setTab("signup")} className="text-primary font-medium hover:underline">Sign up</button>
              </p>
            </form>
          )}

          {tab === "signup" && (
            signupSentTo ? (
              <div className="space-y-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <MailCheck className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h1 className="font-display text-2xl font-bold mb-2">Check your inbox</h1>
                  <p className="text-sm text-muted-foreground">
                    We sent a confirmation link to <span className="font-medium text-foreground">{signupSentTo}</span>. Click it to activate your account.
                  </p>
                </div>
                <div className="rounded-lg border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground space-y-2">
                  <p><strong className="text-foreground">Not there?</strong> Check spam or the Promotions tab, Gmail sometimes hides it.</p>
                  <p>Delivery can take up to a minute.</p>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleResendConfirmation}
                  disabled={resending || resendCooldown > 0}
                >
                  {resending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend confirmation email"}
                </Button>
                <div className="text-center text-sm space-y-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSignupSentTo(null);
                      setEmail("");
                      setPassword("");
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Wrong email? Sign up again
                  </button>
                  <div className="text-muted-foreground/60">·</div>
                  <button
                    type="button"
                    onClick={() => {
                      setSignupSentTo(null);
                      setTab("signin");
                    }}
                    className="text-primary hover:underline"
                  >
                    Already confirmed? Sign in
                  </button>
                </div>
              </div>
            ) : (
            <form onSubmit={handleSignUp} className="space-y-6">
              <div>
                <h1 className="font-display text-2xl font-bold mb-1">Create your account</h1>
                <p className="text-sm text-muted-foreground">Start managing your team in minutes</p>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="name" placeholder="Your name" className="pl-10" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="signup-email" type="email" placeholder="you@team.com" className="pl-10" value={email} onChange={(e) => setEmail(e.target.value)} required readOnly={emailLocked} />
                  </div>
                  {emailLocked && (
                    <p className="text-xs text-muted-foreground">This email is locked to match your team invite.</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="signup-password" type="password" placeholder="Min 6 characters" className="pl-10" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
                  </div>
                </div>
              </div>
              <Button variant="hero" className="w-full" size="lg" type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create account
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <button type="button" onClick={() => setTab("signin")} className="text-primary font-medium hover:underline">Sign in</button>
              </p>
            </form>
            )
          )}

          {tab === "magic" && (
            <form onSubmit={handleMagicLink} className="space-y-6">
              <div>
                <h1 className="font-display text-2xl font-bold mb-1">Magic link</h1>
                <p className="text-sm text-muted-foreground">We'll email you a link to sign in, no password needed</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="magic-email">Email</Label>
                <div className="relative">
                  <Wand2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="magic-email" type="email" placeholder="you@team.com" className="pl-10" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
              </div>
              <Button variant="hero" className="w-full" size="lg" type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Send magic link
              </Button>
              <button type="button" onClick={() => setTab("signin")} className="block mx-auto text-sm text-muted-foreground hover:text-foreground">
                Back to sign in
              </button>
            </form>
          )}

          {tab === "reset" && (
            <form onSubmit={handleReset} className="space-y-6">
              <div>
                <h1 className="font-display text-2xl font-bold mb-1">Reset password</h1>
                <p className="text-sm text-muted-foreground">Enter your email and we'll send you a reset link</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="reset-email" type="email" placeholder="you@team.com" className="pl-10" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
              </div>
              <Button variant="hero" className="w-full" size="lg" type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Send reset link
              </Button>
              <button type="button" onClick={() => setTab("signin")} className="block mx-auto text-sm text-muted-foreground hover:text-foreground">
                Back to sign in
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;
